/**
 * Responsibility: the watchlist, and running it.
 *
 * This page used to be read-only, with a note telling the operator to edit
 * config/products.json. The database is the authoritative watchlist, so this
 * page owns it now: add, tag, pause, resume, remove.
 *
 * Grouping by retailer is the default view because that is how the work
 * actually divides - each retailer has its own adapter, its own block
 * behaviour and its own checkout, so "what am I watching at Costco" is a
 * question asked far more often than "what am I watching in total".
 *
 * Pause is offered alongside remove on purpose. A product between drops is not
 * one you have stopped caring about, and re-adding means finding the URL again.
 */
import { useMemo, useState } from 'react';
import { usePageState } from '../hooks/usePageState';
import { MetricCard, PageContainer, SectionCard } from '@kit/components';
import {
  Alert,
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { AddWatchDialog } from '../components/watchlist/AddWatchDialog';
import { FittedTable } from '../components/shelf/FittedTable';
import { WatchTable } from '../components/watchlist/WatchTable';
import { useDeleteWatch, usePatchWatch, useWatches } from '../api/hooks/useWatches';
import type { Watch } from '../api/client';

const RETAILER_LABELS: Record<string, string> = {
  TARGET: 'Target',
  WALMART: 'Walmart',
  BESTBUY: 'Best Buy',
  COSTCO: 'Costco',
  AMAZON: 'Amazon',
  MOCK: 'Canary',
};

/**
 * The generated schema makes `id` optional because the server supplies a
 * default. Narrowing rather than asserting keeps a malformed row from becoming
 * a request against `/api/watches/undefined`.
 */
function watchId(watch: Watch): string | null {
  return watch.id ?? null;
}

export function WatchlistPage() {
  const { data: watches, isLoading, error } = useWatches();
  const patch = usePatchWatch();
  const remove = useDeleteWatch();

  const [addOpen, setAddOpen] = useState(false);
  const [grouped, setGrouped] = usePageState('watchlist.grouped', true, 'durable');
  const [pendingRemoval, setPendingRemoval] = useState<Watch | null>(null);

  const list = useMemo(() => watches ?? [], [watches]);

  const byRetailer = useMemo(() => {
    const groups = new Map<string, Watch[]>();
    for (const watch of list) {
      const existing = groups.get(watch.retailer);
      if (existing) {
        existing.push(watch);
      } else {
        groups.set(watch.retailer, [watch]);
      }
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [list]);

  const summary = useMemo(
    () => ({
      total: list.length,
      enabled: list.filter((w) => w.enabled).length,
      retailers: new Set(list.map((w) => w.retailer)).size,
      notifyLists: new Set(list.flatMap((w) => w.notify_lists ?? [])).size,
    }),
    [list],
  );

  const toggle = (watch: Watch) => {
    const id = watchId(watch);
    if (!id) return;
    patch.mutate({ watchId: id, patch: { enabled: !watch.enabled } });
  };

  return (
    <PageContainer
    >
      {/* minWidth 0 is load bearing on mobile. A flex or grid item defaults
          to min-width:auto, so the widest thing inside - the watch table, whose
          intrinsic width is its columns added up - drags this column out to
          that width and takes the summary cards with it. The page then slides
          sideways under your thumb. Allowing the column to shrink lets the
          table scroll inside its own container instead, which is the one place
          horizontal scrolling is wanted. */}
      <Stack spacing={3} sx={{ minWidth: 0 }}>

      {error ? <Alert severity="error">{(error as Error).message}</Alert> : null}
      {remove.isError ? <Alert severity="error">{(remove.error as Error).message}</Alert> : null}
      {patch.isError ? <Alert severity="error">{(patch.error as Error).message}</Alert> : null}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        }}
      >
        <MetricCard label="Watches" value={String(summary.total)} />
        <MetricCard
          label="Polling"
          value={String(summary.enabled)}
          helper={summary.enabled === summary.total ? 'all active' : 'some paused'}
        />
        <MetricCard label="Retailers" value={String(summary.retailers)} />
        <MetricCard label="Notify lists" value={String(summary.notifyLists)} />
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={grouped ? 'retailer' : 'flat'}
          onChange={(_event, next) => {
            if (next) setGrouped(next === 'retailer');
          }}
        >
          <ToggleButton value="retailer">By retailer</ToggleButton>
          <ToggleButton value="flat">All</ToggleButton>
        </ToggleButtonGroup>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          Track a product
        </Button>
      </Stack>

      {summary.total === 0 && !isLoading ? (
        <SectionCard title="Watches">
          <Typography variant="body2" color="text.secondary">
            Nothing tracked yet. Open a product page at Target, Walmart, Best Buy, Costco or
            Amazon, then paste its address here with Track a product.
          </Typography>
        </SectionCard>
      ) : grouped ? (
        byRetailer.map(([retailer, rows]) => (
          <SectionCard
            key={retailer}
            title={RETAILER_LABELS[retailer] ?? retailer}
            subtitle={`${rows.filter((r) => r.enabled).length} of ${rows.length} polling`}
          >
            <FittedTable>
              <WatchTable
                rows={rows}
                loading={isLoading}
                busy={patch.isPending}
                showRetailer={false}
                onToggle={toggle}
                onRemove={setPendingRemoval}
              />
            </FittedTable>
          </SectionCard>
        ))
      ) : (
        <SectionCard title="Watches">
          <FittedTable>
            <WatchTable
              rows={list}
              loading={isLoading}
              busy={patch.isPending}
              onToggle={toggle}
              onRemove={setPendingRemoval}
            />
          </FittedTable>
        </SectionCard>
      )}

      <AddWatchDialog open={addOpen} onClose={() => setAddOpen(false)} />

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Stop tracking this?"
        body={
          pendingRemoval
            ? `${pendingRemoval.product_name} at ${pendingRemoval.retailer} will be removed along with its price policy and notify lists. Its event history is kept. If you only want it quiet between drops, pause it instead.`
            : ''
        }
        confirmLabel="Stop tracking"
        pending={remove.isPending}
        onCancel={() => setPendingRemoval(null)}
        onConfirm={() => {
          const id = pendingRemoval ? watchId(pendingRemoval) : null;
          if (!id) return;
          remove.mutate(id, { onSuccess: () => setPendingRemoval(null) });
        }}
      />
      </Stack>
    </PageContainer>
  );
}
